import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  InputAdornment,
  Chip,
  Grid,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CategoryIcon from "@mui/icons-material/Category";
import { toast, ToastContainer } from "react-toastify";
import { useFormik } from "formik";
import api from "../../axiosInstance";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import MentorshipValidation from "../Validations/MentorshipValidation";

const ACCENT = "#17479e";

const whiteSortLabelSx = {
  color: "white !important",
  "&.Mui-active": { color: "white !important" },
  "&:hover": { color: "white !important" },
  "& .MuiTableSortLabel-icon": { color: "white !important" },
};

const columns = [
  { key: "packageId", label: "ID", minWidth: 70, align: "center" },
  { key: "name", label: "Package Name", minWidth: 180 },
  { key: "sessionTypeName", label: "Session Type", minWidth: 160 },
  {
    key: "sessionCount",
    label: "Session Count",
    minWidth: 120,
    align: "center",
  },
  {
    key: "discountPercent",
    label: "Discount %",
    minWidth: 110,
    align: "center",
  },
  { key: "isActive", label: "Status", minWidth: 100, align: "center" },
];

function MentorSessionPackages() {
  const [packages, setPackages] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [sessionTypeFilter, setSessionTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [error, setError] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");

  const load = () => {
    setIsLoading(true);
    const params = { page, size: rowsPerPage };
    if (sessionTypeFilter) params.sessionTypeId = sessionTypeFilter;
    if (activeFilter !== "") params.isActive = activeFilter === "true";

    api
      .get("/admin/mentor-session-packages", { params })
      .then((res) => {
        const raw = res.data?.data;
        setPackages(Array.isArray(raw) ? raw : (raw?.content ?? []));
        setTotalElements(
          Array.isArray(raw) ? raw.length : (raw?.totalElements ?? 0),
        );
      })
      .catch((err) => {
        console.error("Session packages fetch error:", err);
        setError("Failed to load session packages.");
      })
      .finally(() => setIsLoading(false));
  };

  // Reset to first page whenever a filter changes
  useEffect(() => {
    setPage(0);
  }, [sessionTypeFilter, activeFilter]);

  useEffect(() => {
    load();
  }, [sessionTypeFilter, activeFilter, page, rowsPerPage]);

  useEffect(() => {
    api
      .get("/admin/mentor-session-types")
      .then((res) => {
        const raw = res.data?.data;
        setSessionTypes(Array.isArray(raw) ? raw : (raw?.content ?? []));
      })
      .catch((err) => console.error("Session types fetch error:", err));
  }, []);

  const filtered = packages.filter((p) =>
    (p.name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const sortedData = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [filtered, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: editing?.name || "",
      sessionTypeId: editing?.sessionTypeId || "",
      sessionCount: editing?.sessionCount ?? "",
      discountPercent: editing?.discountPercent ?? 0,
      isActive: editing?.isActive ?? true,
    },
    validationSchema: MentorshipValidation.SessionPackageValidationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      const payload = {
        name: values.name.trim(),
        sessionTypeId: Number(values.sessionTypeId),
        sessionCount: Number(values.sessionCount),
        discountPercent: Number(values.discountPercent) || 0,
      };
      try {
        if (editing?.packageId) {
          await api.put(`/admin/mentor-session-packages/${editing.packageId}`, {
            ...payload,
            isActive: values.isActive,
          });
          toast.success("Session package updated Successfully!");
        } else {
          await api.post(`/admin/mentor-session-packages`, payload);
          toast.success("Session package created Successfully!");
        }
        setDialogOpen(false);
        resetForm();
        load();
      } catch (err) {
        toast.error(
          err.response?.data?.errorMessage ||
            "Failed to save session package. Please try again.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/mentor-session-packages/${deleteId}`);
      toast.success("Session package deleted!");
      load();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to delete session package. Please try again.",
      );
    } finally {
      setDeleteId(null);
    }
  };

  const field = (name, label, extra = {}) => ({
    name,
    label,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && formik.errors[name],
    fullWidth: true,
    sx: { "& .MuiOutlinedInput-root": { borderRadius: 2 } },
    ...extra,
  });

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Session Type</InputLabel>
                <Select
                  label="Session Type"
                  value={sessionTypeFilter}
                  onChange={(e) => setSessionTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Session Types</MenuItem>
                  {sessionTypes.map((st) => (
                    <MenuItem key={st.id} value={st.id}>
                      {st.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {sortedData.length} of {totalElements} packages
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
              >
                Add Package
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: 2,
            borderRadius: 3,
          }}
        >
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        minWidth: c.minWidth,
                        padding: "10px 12px",
                        backgroundColor: ACCENT,
                        color: "white",
                        fontWeight: 600,
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      }}
                      sortDirection={orderBy === c.key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === c.key}
                        direction={orderBy === c.key ? order : "asc"}
                        onClick={() => handleSort(c.key)}
                        sx={whiteSortLabelSx}
                      >
                        {c.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{
                      minWidth: 110,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      right: 0,
                      zIndex: 4,
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{ textAlign: "center", py: 4 }}
                    >
                      <Typography variant="body1" color="text.secondary">
                        No session packages found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((row) => (
                    <TableRow
                      key={row.packageId}
                      hover
                      sx={{
                        "&:nth-of-type(odd)": {
                          backgroundColor: "rgba(0,0,0,0.02)",
                        },
                      }}
                    >
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        {row.packageId}
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        <Typography
                          sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                        >
                          {row.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: "10px 12px" }}>
                        {row.sessionTypeName || "—"}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        {row.sessionCount}
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        {row.discountPercent}%
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        <Chip
                          label={row.isActive === false ? "Inactive" : "Active"}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color:
                              row.isActive === false ? "#c62828" : "#2e7d32",
                            backgroundColor:
                              row.isActive === false ? "#ffebee" : "#e8f5e9",
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ padding: "10px 12px" }}>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          justifyContent="center"
                        >
                          <Tooltip title="Edit" arrow>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openEdit(row)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete" arrow>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteId(row.packageId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30, 50]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingLeft: 2,
                paddingRight: 2,
              },
            }}
          />
        </Paper>
      )}

      {/* Add/Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <Box component="form" onSubmit={formik.handleSubmit}>
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.2,
              fontWeight: 700,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: `linear-gradient(135deg, ${ACCENT}, #2f66c9)`,
              }}
            >
              <CategoryIcon sx={{ color: "#fff", fontSize: 18 }} />
            </Box>
            {editing ? "Edit Package" : "Add Package"}
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 2.5 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  {...field("name", "Package Name")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl
                  fullWidth
                  margin="dense"
                  error={
                    formik.touched.sessionTypeId &&
                    Boolean(formik.errors.sessionTypeId)
                  }
                >
                  <InputLabel>Session Type</InputLabel>
                  <Select
                    label="Session Type"
                    name="sessionTypeId"
                    value={formik.values.sessionTypeId}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {sessionTypes.map((st) => (
                      <MenuItem key={st.id} value={st.id}>
                        {st.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("sessionCount", "Session Count")}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  margin="dense"
                  type="number"
                  {...field("discountPercent", "Discount %", {
                    InputProps: {
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    },
                  })}
                />
              </Grid>
              {editing && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.25,
                      borderRadius: 2,
                      border: "1px solid #eef0f4",
                      backgroundColor: "#fbfcfe",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
                        Active
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inactive packages are hidden from booking flows
                      </Typography>
                    </Box>
                    <Switch
                      checked={formik.values.isActive}
                      onChange={(e) =>
                        formik.setFieldValue("isActive", e.target.checked)
                      }
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={() => setDialogOpen(false)}
              {...BUTTON_STYLES.secondary}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={formik.isSubmitting}
              {...BUTTON_STYLES.primary}
            >
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <DeleteConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this session package? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default MentorSessionPackages;
